import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode'; // Install with npm install jwt-decode
import { AuthStatus } from 'type';
import HTTPClient from '../HTTPClient';
import makeRequestWaiter from '../api-clients/ApiClient/makeRequestWaiter';
import { onUnauthorizedResponse } from '../api-clients/ApiClient';

const accountIdCookieName = 'accountId' as const;
type CookieName = 'breathe_access_token' | 'breathe_id_token' | 'breathe_refresh_token' | typeof accountIdCookieName;
const baseURL = process.env.REACT_APP_API_URL as string;
const refreshApiClient = new HTTPClient({
  baseURL,
  withCredentials: true,
});

const refreshToken = makeRequestWaiter(() =>
  refreshApiClient.get('/api/bravo/auth/refresh_token')
);

class AuthService {

  constructor() {
    throw new Error('BreatheSSOService class can\'t be instantiated');
  }

  static getAuthStatus(): AuthStatus {
    return !!this.getIdToken() ? 'authorized' : 'notAuthorized';
  }

  static async getAuthHeaders() {
    type Headers = {
      Authorization: string;
      'x-account-id'?: string;
    };

    const idToken = await this.getValidIdToken();
    if (!idToken) {
      onUnauthorizedResponse();
      return {};
    }

    const headers: Headers = {
      Authorization: `Bearer ${idToken}`,
    };

    const accountId = this.getAccountId();
    if (accountId) {
      headers['x-account-id'] = accountId;
    }
    return headers;
  }

  private static getAccountId() {
    return this.getCookie(accountIdCookieName);
  }

  private static getIdToken() {
    return this.getCookie('breathe_id_token');
  }

  protected static getAccessToken() {
    return this.getCookie('breathe_access_token');
  }

  private static getCookie(cookieName: CookieName) {
    return Cookies.get(cookieName);
  }

  static setAccountId(accountId: string) {
    Cookies.set(accountIdCookieName, accountId);
  }

  static clearAccountId() {
    Cookies.remove(accountIdCookieName);
  }

  static isImpersonated() {
    return !!this.getAccountId();
  }

  private static async getValidIdToken() {
  const token = this.getIdToken();

  if (token) {
    const decoded: { exp: number } = jwtDecode(token);
    const expiryTime = new Date(decoded.exp * 1000); // Convert to milliseconds
    const currentTime = new Date();
    if (expiryTime < currentTime) {
      await this.refreshingToken();
      return this.getCookie('breathe_id_token');
    }
    return token;
  } else {
    await this.refreshingToken();
    return this.getCookie('breathe_id_token');
  }
}

  static async refreshingToken(): Promise<string | null> {
    try {
      await refreshToken();
    } catch (error) {
      console.error('Token refresh failed', error);
    }
    return null;
  }
}

export default AuthService;
